import { QueryFunctionContext, useInfiniteQuery, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import {
  LimitedSchemaInfiniteQueryOptions,
  LimitedSchemaFetchInfiniteQueryOptions,
} from '@frontend/react-query-helpers';
import { SchemaAnalyticsGateway1Service } from '../service';
import { GetScheduleViewIO } from '../types';

export const getGetScheduleViewQueryKey = (req: GetScheduleViewIO['input']) =>
  ['AnalyticsGatewayV1', 'GetScheduleView', req] as const;
type QueryKey = ReturnType<typeof getGetScheduleViewQueryKey>;
type QueryFnArgs = QueryFunctionContext<QueryKey, GetScheduleViewPageParams>;

type GetScheduleViewPageParams = {
  pageToken?: string;
};
/**
 * A hook that returns an infinite query for the `GetScheduleView` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetScheduleViewQuery = <E = unknown, D = GetScheduleViewIO['output']>(
  req: GetScheduleViewIO['input'],
  options?: Omit<
    LimitedSchemaInfiniteQueryOptions<GetScheduleViewIO, E, D, QueryKey>,
    'getNextPageParam' | 'getPreviousPageParam'
  >,
  httpOptions?: Options
) =>
  useInfiniteQuery<GetScheduleViewIO['output'], E, D, QueryKey>({
    queryKey: getGetScheduleViewQueryKey(req),
    queryFn: ({ pageParam }: QueryFnArgs) => {
      return SchemaAnalyticsGateway1Service.GetScheduleView(
        {
          ...req,
          pageToken: pageParam?.pageToken,
        },
        httpOptions
      );
    },
    ...options,
    getNextPageParam: (lastPage): GetScheduleViewPageParams | undefined => {
      if (lastPage.nextPageToken) {
        return { pageToken: lastPage.nextPageToken };
      }
      return undefined;
    },
    getPreviousPageParam: undefined,
  });

/**
 * A hook that returns a function to fetch the query for the `GetScheduleView` endpoint.
 * It uses `queryClient.fetchInfiniteQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useInfiniteQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchGetScheduleView = () => {
  const queryClient = useQueryClient();

  const fetchGetScheduleView = async <E = unknown, D = GetScheduleViewIO['output']>(
    req: GetScheduleViewIO['input'],
    options?: LimitedSchemaFetchInfiniteQueryOptions<GetScheduleViewIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchInfiniteQuery(
      getGetScheduleViewQueryKey(req),
      () => SchemaAnalyticsGateway1Service.GetScheduleView(req, httpOptions),
      options
    );

    return response;
  };

  return fetchGetScheduleView;
};
